import { useMemo, useState } from "react";
import { Zap } from "lucide-react";

const EXTRA_OPTIONS = [
  { id: "dpf_off", label: "DPF OFF", desc: "Diesel Particulate Filter removal", diesel: true },
  { id: "egr_off", label: "EGR OFF", desc: "Exhaust Gas Recirculation deactivation", diesel: true },
  { id: "adblue_off", label: "AdBlue OFF", desc: "SCR/urea system deactivation", diesel: true },
  { id: "swirl_flap", label: "Swirl Flap OFF", desc: "Swirl flap deactivation", diesel: true },
  { id: "hot_start_fix", label: "Hot Start Fix", desc: "Fix hot start issues on diesel engines", diesel: true },
  { id: "pops_bangs", label: "Pops & Bangs", desc: "Exhaust crackle & overrun effects", petrol: true },
  { id: "hardcut_limiter", label: "Hardcut Limiter", desc: "Hardcut rev limiter with bang effect", petrol: true },
  { id: "launch_control", label: "Launch Control", desc: "Launch control activation", petrol: true },
  { id: "secondary_cat_off", label: "Secondary CAT OFF", desc: "Secondary catalytic converter removal", petrol: true },
  { id: "dtc_off", label: "DTC OFF", desc: "Fault code removal/deactivation", all: true },
  { id: "o2_nox_off", label: "O2/NOx OFF", desc: "Oxygen & NOx sensor deactivation", all: true },
  { id: "decat", label: "DECAT", desc: "Catalytic converter software removal", all: true },
  { id: "vmax_off", label: "VMAX OFF", desc: "Speed limiter removal", all: true },
  { id: "tcu_tune", label: "TCU Tuning", desc: "Transmission/DSG/gearbox optimization", all: true },
  { id: "start_stop_off", label: "Start/Stop OFF", desc: "Disable auto start-stop system", all: true },
  { id: "exhaust_flap", label: "Exhaust Flap OFF", desc: "Exhaust flap deactivation", all: true },
  { id: "immo_off", label: "IMMO OFF", desc: "Immobilizer deactivation", all: true },
  { id: "multimap", label: "Multi-Map Switch", desc: "Switchable performance maps", all: true },
  { id: "ecu_clone", label: "ECU Clone", desc: "Clone ECU data to replacement unit", all: true },
  { id: "back_to_stock", label: "Back to Stock", desc: "Restore original ECU mapping", all: true },
];

export function useExtraOptions(fuelType) {
  const [selectedExtras, setSelectedExtras] = useState([]);

  const filteredOptions = useMemo(() => {
    const isDiesel = fuelType === "Diesel";
    return EXTRA_OPTIONS.filter(
      (opt) => opt.all || (opt.diesel && isDiesel) || (opt.petrol && !isDiesel)
    );
  }, [fuelType]);

  const toggleExtra = (id) => {
    setSelectedExtras((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const resetExtras = () => setSelectedExtras([]);

  return { selectedExtras, filteredOptions, toggleExtra, resetExtras };
}

export default function ExtraOptions({ filteredOptions, selectedExtras, toggleExtra }) {
  return (
    <div data-testid="extra-options" className="mt-6">
      <div className="flex items-center gap-2 mb-3">
        <Zap size={16} className="text-[#FF3B30]" />
        <span className="text-xs text-[#8A8A8A] uppercase tracking-wider">Extra Options</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {filteredOptions.map((opt) => {
          const isSelected = selectedExtras.includes(opt.id);
          return (
            <button
              key={opt.id}
              data-testid={`extra-${opt.id}`}
              onClick={() => toggleExtra(opt.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                isSelected
                  ? "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/50"
                  : "bg-[#1A1A1A] text-[#8A8A8A] border-[#2A2A2A] hover:border-[#FF3B30]/30 hover:text-white"
              }`}
              title={opt.desc}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {selectedExtras.length > 0 && (
        <p className="text-[#8A8A8A] text-xs mt-2">
          {selectedExtras.length} extra option{selectedExtras.length > 1 ? "s" : ""} selected
        </p>
      )}
      <p className="text-[#6A6A6A] text-xs mt-3 italic">Need an option not listed? Mention it in the contact form.</p>
    </div>
  );
}
